import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import get from "lodash/get";
import "./expert-speaks-section.m.css";
import { useModalEffect } from "../../../hooks/useModalEffect";
import { VideoCard } from "../video-card";

const ExpertSpeaksSection = (props) => {
  const [videoModal, setVideoModal] = useState({ isOpen: false, embedUrl: '' });
  const [vimeoThumbnails, setVimeoThumbnails] = useState({});
  const [openTextCard, setOpenTextCard] = useState(null);
  const cdnHost = useSelector((state) => get(state, ["qt", "config", "cdn-image"]));
  const collection = props.collection;
  const expertSpeakscollection = collection?.items?.find(item => item?.slug === "expert-speaks");

  function getYouTubeVideoId(url) {
    if (!url) return null;

    try {
      const parsedUrl = new URL(url);
      if (parsedUrl.hostname.includes("youtu.be")) {
        return parsedUrl.pathname.slice(1);
      }
      // youtube.com/watch?v=<id>
      if (parsedUrl.searchParams.get("v")) {
        return parsedUrl.searchParams.get("v");
      }
      // youtube.com/embed/<id>
      if (parsedUrl.pathname.includes("/embed/")) {
        return parsedUrl.pathname.split("/embed/")[1];
      }
      return null;
    } catch {
      return null;
    }
  }

  const fetchVimeoThumbnail = async (vimeoUrl, videoId) => {
    try {
      const response = await fetch(`https://vimeo.com/api/oembed.json?url=${vimeoUrl}`);
      const data = await response.json();
      setVimeoThumbnails(prev => ({ ...prev, [videoId]: data.thumbnail_url }));
    } catch (error) {
      console.error('Error fetching Vimeo thumbnail:', error);
    }
  };

  //display first 2 video template cards and first 2 non-video cards
  const videoItems = expertSpeakscollection?.items?.filter(item => item?.story?.['story-template'] === 'video')?.slice(0, 2) || [];
  const nonVideoItems = expertSpeakscollection?.items?.filter(item => item?.story?.['story-template'] !== 'video')?.slice(0, 2) || [];
  const expertSpeaksStoriesToDisplay = [...videoItems, ...nonVideoItems];

  useEffect(() => {
    if (expertSpeaksStoriesToDisplay) {
      expertSpeaksStoriesToDisplay.forEach(item => {
        const { story } = item || {};
        const { cards = [] } = story || {};
        const [firstElement] = cards[0]?.['story-elements'] || [];

        if (firstElement?.subtype === "vimeo-video") {
          const videoId = firstElement?.metadata?.['video-id'];
          const vimeoUrl = firstElement?.metadata?.['vimeo-url'] || firstElement?.url;
          if (videoId && vimeoUrl && !vimeoThumbnails[videoId]) {
            fetchVimeoThumbnail(vimeoUrl, videoId);
          }
        }
      });
    }
  }, [expertSpeaksStoriesToDisplay]);

  const expertSpeaksData = expertSpeaksStoriesToDisplay?.map(item => {
    const { story } = item || {};
    const { cards = [], metadata = {}, 'hero-image-s3-key': heroImageKey, url: storyUrl } = story || {};
    const [firstElement, secondElement] = cards[0]?.['story-elements'] || [];

    const type = story?.['story-template'];
    const videoEmbed = firstElement?.['embed-url'] || `https://player.vimeo.com/video/${firstElement?.metadata?.['video-id']}`;
    const videoUrl = firstElement?.url || firstElement?.metadata?.['vimeo-url'];
    const videoDesc = secondElement?.text || '';
    const videoId = firstElement?.subtype === "vimeo-video" ? firstElement?.metadata?.['video-id'] : getYouTubeVideoId(videoUrl);
    const thumbnail = firstElement?.subtype === "vimeo-video" ? (vimeoThumbnails[videoId] || "") : `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    const heroImageUrl = heroImageKey ? `https://${cdnHost}/${heroImageKey}` : '';

    const baseData = {
      id: item?.id,
      type: type === 'video' ? 'video' : 'article',
      category: story?.subheadline,
      title: story?.headline,
      date: metadata?.when,
    };

    return type === 'video' ? {
      ...baseData,
      speakerName: metadata?.expert,
      speakerPosition: metadata?.['expert-description'],
      playButton: true,
      videoEmbed,
      videoDesc,
      videoThumbnail: thumbnail,
    } : {
      ...baseData,
      writerName: metadata?.expert,
      writerPosition: metadata?.['expert-description'],
      articleUrl: storyUrl,
      articleImage: heroImageUrl,
    };
  });

  const handlePlayVideo = (videoEmbed) => {
    setVideoModal({ isOpen: true, embedUrl: videoEmbed });
  };

  useModalEffect(videoModal.isOpen);

  const handleCardToggle = (cardId) => {
    setOpenTextCard(openTextCard === cardId ? null : cardId);
  };

  const VideoModal = ({ isOpen, embedUrl, onClose }) => {
    if (!isOpen) return null;

    return (
      <div styleName="modal-overlay" onClick={onClose}>
        <div styleName="modal-content" onClick={(e) => e.stopPropagation()}>
          <button styleName="close-button" onClick={onClose}>×</button>
          <div styleName="video-container">
            <iframe
              src={embedUrl}
              title="Video Player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              styleName="video-iframe"
            ></iframe>
          </div>
        </div>
      </div>
    );
  };

  const ArticleCard = ({ item }) => (
    <div styleName="card-wrapper">
      <div styleName="card-header">
        <span styleName="category">{item.category}</span>
        <span styleName="divider">|</span>
        <span styleName="date">{item.date}</span>
      </div>
      <div styleName="article-card">
        <div styleName="article-image">
          <img
            src={item.articleImage}
            alt={item.title}
            styleName="thumbnail-image"
          />
        </div>
        <div styleName="article-content">
          <div styleName="article-title-wrapper">
            <h3 styleName="article-title">{item.title}</h3>
            <a href={item.articleUrl} styleName="read-article-link">
              <span styleName="link-text">Read article</span>
            </a>
          </div>
          <div styleName="writer-info">
            <div styleName="writer-name">{item.writerName}</div>
            <div styleName="writer-position">{item.writerPosition}</div>
          </div>
        </div>
      </div>
      <a href={item.articleUrl} styleName="external-read-article-link">
        <span styleName="external-link-text">Read article</span>
      </a>
    </div>
  );

  return (
    <div styleName="expert-speaks-section">
      <div styleName="container">
        <div styleName="header">
          <h2 styleName="section-heading">Expertspeak</h2>
        </div>

        <div styleName="cards-grid">
          <div styleName="video-section">
            {expertSpeaksData?.filter(item => item.type === "video").map((item) => (
              <div key={item.id} styleName="card-item">
                <VideoCard
                  item={item}
                  isTextView={openTextCard === item.id}
                  onToggle={handleCardToggle}
                  onPlayVideo={handlePlayVideo}
                />
              </div>
            ))}
          </div>

          <div styleName="article-section">
            {expertSpeaksData?.filter(item => item.type !== "video").map((item) => (
              <div key={item.id} styleName="card-item">
                <ArticleCard item={item} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <VideoModal
        isOpen={videoModal.isOpen}
        embedUrl={videoModal.embedUrl}
        onClose={() => setVideoModal({ isOpen: false, embedUrl: '' })}
      />
    </div>
  );
};

export { ExpertSpeaksSection };